import { useState } from "react";

import { Calendar, MessageCircle, MoreHorizontal, X } from "lucide-react";
import { useNavigate } from "react-router";

import { useCurrentUser } from "@/components/helpers/custom-hooks/use-current-user";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Assignment } from "@/types/coaches";

import { RemoveCoach } from "./RemoveCoach";

export type AssignmentCoach = NonNullable<Assignment["coach"]>;

interface CoachMenuProps {
  userId: string;
  coach: AssignmentCoach;
  assignment: Assignment;
  chatChannelId: string;
  viewAsCoach: boolean;
  onRemove?: () => void;
}

export function CoachMenu({
  userId,
  coach,
  assignment,
  chatChannelId,
  viewAsCoach,
  onRemove,
}: CoachMenuProps) {
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const [showingWarning, setShowingWarning] = useState(false);
  const scheduleLink = viewAsCoach
    ? `/schedule/${coach.id}?clientId=${userId}`
    : `/schedule/${coach.id}`;
  const chatLink = `/messages/${userId}/channels/${chatChannelId}`;
  const isSelf = currentUser?.id === coach.id;

  const canAction =
    !viewAsCoach || (viewAsCoach && (isSelf || currentUser?.isStaff));

  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="minimal"
            size="lg"
            className="h-8 w-8 p-0 text-slate-500 hover:text-slate-700"
          >
            <MoreHorizontal className="h-5 w-5" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem
            onClick={() => {
              navigate(chatLink);
              setIsOpen(false);
            }}
            className="cursor-pointer"
          >
            <MessageCircle className="mr-2 h-4 w-4" />
            Message Coach
          </DropdownMenuItem>
          {canAction && (
            <>
              <DropdownMenuItem
                onClick={() => {
                  navigate(scheduleLink);
                  setIsOpen(false);
                }}
                className="cursor-pointer"
              >
                <Calendar className="mr-2 h-4 w-4" />
                Schedule Session
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => {
                  setShowingWarning(true);
                  setIsOpen(false);
                }}
                className="cursor-pointer text-red-600 focus:text-red-600"
              >
                <X className="mr-2 h-4 w-4 text-red-600" />
                Remove Coach
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <RemoveCoach
        showingWarning={showingWarning}
        onCancel={() => {
          setShowingWarning(false);
        }}
        onRemove={onRemove}
        assignment={assignment}
        coach={coach}
        userId={userId}
        viewAsCoach={viewAsCoach}
      />
    </>
  );
}
